import { IModule, IQService } from 'angular';
import { ConfigService } from 'app/config/config.service';
import { EventRegistry } from 'app/shared/events/event_registry';
import momentDurationFormatSetup from 'moment-duration-format';
import moment from 'moment-timezone';
import sortable from 'sortablejs';
import { LOCAL_STORAGE_TOKEN_KEY } from 'app/agriculture/contexts/RequiredStartupProviders/TokenContext/TokenContextProvider';

function handleRejectedPromise() {
    window.addEventListener('unhandledrejection', function (e: any) {
        // NOTE: e.preventDefault() must be manually called to prevent the default
        // action which is currently to log the stack trace to console.warn
        e.preventDefault();
        const reason =
            (e && e.detail && e.detail.reason && e.detail.reason.message) ||
            (e && e.reason && e.reason.message) ||
            '';
        const stack =
            (e && e.detail && e.detail.reason && e.detail.reason.stack) ||
            (e && e.reason && e.reason.stack) ||
            '';
        // Log the error and stack trace because when running unit tests we lose the ability to inspect the error.
        console.error(`An error has occurred\n${reason}\n${stack}`);
        // Now rethrow the error. This will make it inspectable in the console window
        throw e;
    });
}

function setupMoment(moment) {
    momentDurationFormatSetup(moment);
    console.assert(typeof moment.duration.fn.format === 'function');
    console.assert(typeof moment.duration.format === 'function');

    // Patch application specific stuff onto moment
    const TWENTY_FOUR_HOUR_LONG_DATE_FORMAT = {
        LT: 'HH:mm',
        LTS: 'HH:mm:ss',
        L: 'MM/DD/YYYY',
        l: 'M/D/YYYY',
        LL: 'MMMM Do YYYY',
        ll: 'MMM D YYYY',
        LLL: 'MMMM Do YYYY LT',
        lll: 'MMM D YYYY LT',
        LLLL: 'dddd, MMMM Do YYYY LT',
        llll: 'ddd, MMM D YYYY LT',
    };

    moment.updateLocale('en', {
        longDateFormat: TWENTY_FOUR_HOUR_LONG_DATE_FORMAT,
    });
}

export function standardApplicationBootstrapping() {
    handleRejectedPromise();

    require('../assets/external_scripts/keypress-2.1.4.min');

    setupMoment(moment);
}

// I just copied this from stack overflow
function readCookie(name) {
    var nameEQ = name + '=';
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

export const HTTP_INTERCEPTOR_FACTORY_NAME = 'timeoutHttpIntercept';

export function withHTTPInterceptor(
    module: IModule,
    onForbiddenRequestEvent: EventRegistry,
    onUnauthorizedRequestEvent: EventRegistry,
) {
    module.factory(HTTP_INTERCEPTOR_FACTORY_NAME, [
        '$q',
        function ($q: IQService) {
            return {
                request: function (request) {
                    const csrfToken = readCookie('csrftoken');
                    const apiToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);

                    if (apiToken) {
                        request.headers.Authorization = `Token ${apiToken}`;
                    }

                    if (csrfToken) {
                        request.headers['X-CSRFToken'] = csrfToken;
                    }
                    // timeout in ms
                    // The API will cut any connections at 50 seconds
                    // This is for the case where the API never responds or spotty connections
                    // makes lots and lots of tcp retries.
                    // Leaving writes and reads separate for later fine tuning.
                    if (request.method === 'GET') {
                        request.timeout = ConfigService.maxGetTimeout;
                    } else if (['PATCH', 'POST', 'PUT', 'DELETE'].includes) {
                        request.timeout = ConfigService.maxPostPutPatchDeleteTimeout;
                    }
                    // else....no timeout
                    return request;
                },
                responseError: (rejection) => {
                    if (rejection && rejection.status == 403 && rejection.config.method === 'GET') {
                        // display logout prompt if forbidden and request was a get request (means user
                        // is not part of any ims groups)
                        onForbiddenRequestEvent.notifyListeners();
                    }
                    if (rejection && rejection.status == 401) {
                        onUnauthorizedRequestEvent.notifyListeners();
                    }
                    return $q.reject(rejection);
                },
            };
        },
    ]);
}
